import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tab,
  Tabs,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Snackbar,
  Alert,
  InputAdornment,
  Switch,
  FormControlLabel,
  Divider,
  CircularProgress,
  TableSortLabel,
  Tooltip,
  Checkbox,
  SelectChangeEvent,
  ListItemText,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  AttachMoney as AttachMoneyIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Repeat as RepeatIcon,
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CalendarToday as CalendarTodayIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Refresh as RefreshIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
  Feedback as FeedbackIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useAuth } from '../context/AuthContext';
import { CloudTenantDataService } from '../services/tenant-data-service';
import { isAdminEmail } from '../config/admin';
import * as indexedDBService from '../utils/indexedDBService';
import { Expense, RecurringExpense, EXPENSE_CATEGORIES, PAYMENT_METHODS, FREQUENCIES } from '../types/expense';
import { EmployeeAdvance, RefundRecord } from '../types/advance';
import { format } from 'date-fns';
import AnimatedPage from '../components/AnimatedPage';

// TabPanel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`expense-tabpanel-${index}`}
      aria-labelledby={`expense-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const Expenses: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);

  // Role Checks
  const isStaffMember = userProfile?.role !== 'admin' && userProfile?.role !== 'super_admin' && userProfile?.role !== 'accountant' && !isAdminEmail(currentUser?.email || '');
  const isAdminOrAccountant = !isStaffMember;

  // Cloud Unified Tenant Database
  const tenantId = currentUser?.tenantId || userProfile?.tenantId || currentUser?.uid;
  const db = React.useMemo(() => new CloudTenantDataService(tenantId || 'default'), [tenantId]);

  // Expenses state
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [recurringExpenses, setRecurringExpenses] = useState<RecurringExpense[]>([]);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [monthlyExpenses, setMonthlyExpenses] = useState(0);
  const [loading, setLoading] = useState(true);

  // Rejection Dialog State
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [rejectionExpenseId, setRejectionExpenseId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Status Filter State
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');

  // Dialog state
  const [openExpenseDialog, setOpenExpenseDialog] = useState(false);
  const [openRecurringDialog, setOpenRecurringDialog] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [selectedRecurring, setSelectedRecurring] = useState<RecurringExpense | null>(null);

  // Advance state
  const [openAdvanceDialog, setOpenAdvanceDialog] = useState(false);
  const [staffList, setStaffList] = useState<any[]>([]);
  const [advances, setAdvances] = useState<EmployeeAdvance[]>([]);
  const [activeAdvanceBalance, setActiveAdvanceBalance] = useState(0);
  const [newAdvance, setNewAdvance] = useState({
    staffUid: '',
    staffName: '',
    amount: 0,
    reason: '',
    date: new Date().toISOString().split('T')[0],
    paymentMethod: ''
  });

  // Return Advance State
  const [openReturnDialog, setOpenReturnDialog] = useState(false);
  const [selectedReturnAdvance, setSelectedReturnAdvance] = useState<EmployeeAdvance | null>(null);
  const [returnAmount, setReturnAmount] = useState<number>(0);
  const [returnMethod, setReturnMethod] = useState<'Cash' | 'UPI' | 'Bank'>('Cash');
  const [returnReference, setReturnReference] = useState<string>('');
  const [returnProofFile, setReturnProofFile] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ start: Date | null, end: Date | null }>({
    start: null,
    end: null
  });

  // Sort state
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({ key: 'date', direction: 'desc' });

  // Form state
  const [newExpense, setNewExpense] = useState<Partial<Expense>>({
    title: '',
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    category: '',
    paymentMethod: '',
    reference: '',
    notes: '',
    fileUrl: '',
    accountNumber: '',
    chequeNumber: '',
    settlementMethod: 'out_of_pocket',
  });

  const [newRecurring, setNewRecurring] = useState<Partial<RecurringExpense>>({
    title: '',
    amount: 0,
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    frequency: 'monthly',
    category: '',
    paymentMethod: '',
    notes: '',
    isActive: true,
  });

  // File upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileUploadProgress, setFileUploadProgress] = useState(0);

  // Notification state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12
      }
    }
  };

  // Real-time Background Polling & Refresh (30-second interval)
  useEffect(() => {
    if (!currentUser) return;
    
    fetchExpenses();
    fetchRecurringExpenses();
    fetchAdvances();
    if (isAdminOrAccountant) fetchStaffList();

    const intervalId = setInterval(() => {
      fetchExpenses();
      fetchRecurringExpenses();
      fetchAdvances();
    }, 30000); // 30 seconds

    return () => clearInterval(intervalId);
  }, [currentUser, tenantId]);

  const fetchStaffList = async () => {
    try {
      const response = await fetch(`http://localhost:5000/api/staff?tenantId=${tenantId}`);
      const data = await response.json();
      if (data.success && data.users) {
        const formatted = data.users.map((cognitoUser: any) => {
          const attributes = cognitoUser.Attributes.reduce((acc: any, attr: any) => {
            acc[attr.Name] = attr.Value;
            return acc;
          }, {});
          return {
            uid: cognitoUser.Username,
            email: attributes['email'],
            displayName: attributes['name'] || attributes['email']?.split('@')[0],
          };
        });
        setStaffList(formatted);
      }
    } catch (e) {
      console.error('Error fetching staff', e);
    }
  };

  const fetchAdvances = async () => {
    try {
      let data = await db.getAll<EmployeeAdvance>('employee_advances');
      
      // Data privacy: Non-admins only see their own advances.
      // Use the broadest possible matching since Cognito Username can be either
      // a UUID (sub) or an email string depending on the pool configuration.
      if (isStaffMember && currentUser) {
        const userEmail = (currentUser.email || '').toLowerCase();
        const userUid = currentUser.uid;
        const userDisplayName = (userProfile?.displayName || '').toLowerCase();
        data = data.filter(a =>
          // Match by Cognito sub UUID
          a.staffUid === userUid ||
          // Match when Cognito Username is the email address
          (a.staffUid && a.staffUid.toLowerCase() === userEmail) ||
          // Match by saved staffEmail field (new advances)
          (a.staffEmail && a.staffEmail.toLowerCase() === userEmail) ||
          // Last resort: match by normalized display name (handles spaces e.g. "AKMO Solutions" vs "akmosolutions")
          (userDisplayName && a.staffName && (
            a.staffName.toLowerCase().replace(/\s+/g, '') === userDisplayName.replace(/\s+/g, '') ||
            a.staffName.toLowerCase().replace(/\s+/g, '').includes(userDisplayName.replace(/\s+/g, '')) ||
            userDisplayName.replace(/\s+/g, '').includes(a.staffName.toLowerCase().replace(/\s+/g, ''))
          ))
        );
      }
      
      // Sort advances by date descending
      data.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      
      setAdvances(data);
      
      if (isStaffMember && currentUser) {
        const myActive = data.filter(a => a.status === 'pending_settlement');
        if (myActive.length > 0) {
          setNewExpense(prev => ({ ...prev, settlementMethod: 'against_advance' }));
        }
      }
    } catch (e) {
      console.error('Error fetching advances', e);
    }
  };

  // ── Derived: unspent advance balance for current staff member ──────────────
  // Counts ALL approved expenses (any settlement method) against active advances.
  const myUnspentBalance = React.useMemo(() => {
    if (!isStaffMember || !currentUser) return 0;
    const totalAdvances = advances
      .filter(a => a.status === 'pending_settlement')
      .reduce((sum, a) => sum + a.amount, 0);
    const totalApprovedExpenses = expenses
      .filter(e => e.status === 'approved')
      .reduce((sum, e) => sum + e.amount, 0);
    const totalApprovedRefunds = advances
      .flatMap(a => a.returnHistory || [])
      .filter(r => r.status === 'approved')
      .reduce((sum, r) => sum + r.amount, 0);
    return Math.max(0, totalAdvances - totalApprovedExpenses - totalApprovedRefunds);
  }, [advances, expenses, isStaffMember, currentUser]);

  // Helper: compute unrecovered balance for a specific advance (admin table view)
  const computeUnrecoveredBalance = React.useCallback((advance: EmployeeAdvance): number => {
    const sumExpenses = expenses
      .filter(e => {
        if (e.status !== 'approved') return false;
        // 1. UID match
        if (e.submittedByUid === advance.staffUid) return true;
        // 2. Email match (case-insensitive)
        if (e.submittedByEmail && advance.staffEmail && e.submittedByEmail.toLowerCase() === advance.staffEmail.toLowerCase()) return true;
        // 3. Normalized name match fallback (handles dummy/legacy onboarding profiles)
        if (e.submittedByName && advance.staffName) {
          const eName = e.submittedByName.toLowerCase().replace(/\s+/g, '');
          const aName = advance.staffName.toLowerCase().replace(/\s+/g, '');
          if (eName === aName || eName.includes(aName) || aName.includes(eName)) return true;
        }
        return false;
      })
      .reduce((sum, e) => sum + e.amount, 0);
    const sumApprovedRefunds = (advance.returnHistory || [])
      .filter(r => r.status === 'approved')
      .reduce((sum, r) => sum + r.amount, 0);
    return Math.max(0, advance.amount - sumExpenses - sumApprovedRefunds);
  }, [expenses]);

  // Helper: get the pending_admin refund for an advance (if any)
  const getPendingRefund = (advance: EmployeeAdvance): RefundRecord | undefined =>
    (advance.returnHistory || []).find(r => r.status === 'pending_admin');

  // Helper: get the latest rejected refund (so staff sees what was rejected)
  const getLatestRejectedRefund = (advance: EmployeeAdvance): RefundRecord | undefined => {
    const rejected = (advance.returnHistory || [])
      .filter(r => r.status === 'rejected')
      .sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());
    return rejected[0];
  };

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Fetch expenses from DynamoDB
  const fetchExpenses = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      let expensesData = await db.getAll<Expense>('expenses');

      // ─── Data Privacy Filtering (Crucial for Staff View) ───
      // Non-admins strictly only see records where submittedByUid === currentUser.uid
      if (isStaffMember) {
        expensesData = expensesData.filter(expense => expense.submittedByUid === currentUser.uid);
      }

      // Sort in memory (DynamoDB scan is unordered)
      expensesData.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setExpenses(expensesData);

      // Calculate totals based on filtered expenses (approved or legacy only)
      const total = expensesData
        .filter(expense => !expense.status || expense.status === 'approved')
        .reduce((sum, expense) => sum + expense.amount, 0);
      setTotalExpenses(total);

      // Calculate monthly expenses (current month, approved/legacy only)
      const now = new Date();
      const thisMonth = now.getMonth();
      const thisYear = now.getFullYear();

      const monthlyTotal = expensesData
        .filter(expense => !expense.status || expense.status === 'approved')
        .filter(expense => {
          const expenseDate = new Date(expense.date);
          return expenseDate.getMonth() === thisMonth && expenseDate.getFullYear() === thisYear;
        })
        .reduce((sum, expense) => sum + expense.amount, 0);

      setMonthlyExpenses(monthlyTotal);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      setSnackbar({
        open: true,
        message: 'Error loading expenses',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch recurring expenses from DynamoDB
  const fetchRecurringExpenses = async () => {
    if (!currentUser) return;

    try {
      const recurringData = await db.getAll<RecurringExpense>('recurringExpenses');
      // Sort by startDate desc
      recurringData.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());

      setRecurringExpenses(recurringData);
    } catch (error) {
      console.error('Error fetching recurring expenses:', error);
    }
  };

  // Filter expenses based on search query, category, and status filter
  const getFilteredExpenses = () => {
    return expenses.filter(expense => {
      // Search filter
      const searchMatch = !searchQuery ||
        expense.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        expense.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        expense.paymentMethod.toLowerCase().includes(searchQuery.toLowerCase());

      // Category filter
      const categoryMatch = categoryFilter.length === 0 ||
        categoryFilter.includes(expense.category);

      // Date range filter
      let dateMatch = true;
      if (dateRange.start && dateRange.end) {
        const expenseDate = new Date(expense.date);
        dateMatch = expenseDate >= dateRange.start && expenseDate <= dateRange.end;
      }

      // Status Filter Matcher
      let statusMatch = true;
      if (statusFilter === 'pending') {
        statusMatch = expense.status === 'pending';
      } else if (statusFilter === 'approved') {
        statusMatch = !expense.status || expense.status === 'approved';
      } else if (statusFilter === 'rejected') {
        statusMatch = expense.status === 'rejected';
      }

      return searchMatch && categoryMatch && dateMatch && statusMatch;
    });
  };

  // Sort expenses
  const getSortedExpenses = () => {
    const filteredExpenses = getFilteredExpenses();

    if (sortConfig.key) {
      return [...filteredExpenses].sort((a, b) => {
        // Handle undefined or null values
        const valueA = a[sortConfig.key as keyof Expense];
        const valueB = b[sortConfig.key as keyof Expense];

        if (valueA === undefined && valueB === undefined) return 0;
        if (valueA === undefined) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valueB === undefined) return sortConfig.direction === 'asc' ? 1 : -1;

        if (valueA < valueB) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (valueA > valueB) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredExpenses;
  };

  // Request sorting change
  const requestSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Export expenses to CSV
  const exportToCSV = () => {
    if (expenses.length === 0) return;

    const headers = ['Title', 'Amount', 'Date', 'Category', 'Payment Method', 'Reference', 'Notes'];

    const csvRows = [
      headers.join(','),
      ...getSortedExpenses().map(expense => {
        return [
          expense.title,
          expense.amount.toString(),
          expense.date,
          expense.category,
          expense.paymentMethod,
          expense.reference || '',
          expense.notes || ''
        ].join(',');
      })
    ];

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `expenses_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle opening the expense dialog
  const handleOpenExpenseDialog = (expense?: Expense) => {
    if (expense) {
      setSelectedExpense(expense);
      setNewExpense(expense);
    } else {
      setSelectedExpense(null);
      setNewExpense({
        title: '',
        amount: 0,
        date: new Date().toISOString().split('T')[0],
        category: '',
        paymentMethod: '',
        reference: '',
        notes: '',
        fileUrl: '',
        accountNumber: '',
        chequeNumber: '',
      });
    }
    setSelectedFile(null);
    setOpenExpenseDialog(true);
  };

  // Handle closing the expense dialog
  const handleCloseExpenseDialog = () => {
    setOpenExpenseDialog(false);
    setSelectedExpense(null);
    setSelectedFile(null);
  };

  // Handle opening the recurring expense dialog
  const handleOpenRecurringDialog = (recurring?: RecurringExpense) => {
    if (recurring) {
      setSelectedRecurring(recurring);
      setNewRecurring(recurring);
    } else {
      setSelectedRecurring(null);
      setNewRecurring({
        title: '',
        amount: 0,
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        frequency: 'monthly',
        category: '',
        paymentMethod: '',
        notes: '',
        isActive: true,
      });
    }
    setOpenRecurringDialog(true);
  };

  // Handle closing the recurring expense dialog
  const handleCloseRecurringDialog = () => {
    setOpenRecurringDialog(false);
    setSelectedRecurring(null);
  };

  // Handle input change for expense form
  const handleExpenseInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewExpense({
      ...newExpense,
      [name]: name === 'amount' ? parseFloat(value) || 0 : value
    });
  };

  // Handle select change for expense form
  const handleExpenseSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setNewExpense({
      ...newExpense,
      [name as string]: value
    });
  };

  // Handle date change for expense form
  const handleExpenseDateChange = (date: Date | null) => {
    if (date) {
      setNewExpense({
        ...newExpense,
        date: date.toISOString().split('T')[0]
      });
    }
  };

  // Handle input change for recurring expense form
  const handleRecurringInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewRecurring({
      ...newRecurring,
      [name]: name === 'amount' ? parseFloat(value) || 0 : value
    });
  };

  // Handle switch change for recurring expense form
  const handleRecurringSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewRecurring({
      ...newRecurring,
      isActive: e.target.checked
    });
  };

  // Handle select change for recurring expense form
  const handleRecurringSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setNewRecurring({
      ...newRecurring,
      [name as string]: value
    });
  };

  // Handle date change for recurring expense form
  const handleRecurringStartDateChange = (date: Date | null) => {
    if (date) {
      setNewRecurring({
        ...newRecurring,
        startDate: date.toISOString().split('T')[0]
      });
    }
  };

  // Handle end date change for recurring expense form
  const handleRecurringEndDateChange = (date: Date | null) => {
    if (date) {
      setNewRecurring({
        ...newRecurring,
        endDate: date.toISOString().split('T')[0]
      });
    } else {
      setNewRecurring({
        ...newRecurring,
        endDate: ''
      });
    }
  };

  // Handle file selection for attachments
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  // Helper to convert file to Base64
  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  // Handle saving an expense
  const handleSaveExpense = async () => {
    if (!currentUser) return;

    if (!newExpense.title || !newExpense.amount || !newExpense.date || !newExpense.category || !newExpense.paymentMethod) {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields',
        severity: 'error'
      });
      return;
    }

    try {
      let fileUrl = newExpense.fileUrl || '';

      // Convert file to Base64 if selected
      if (selectedFile) {
        fileUrl = await convertFileToBase64(selectedFile);
      }

      let expenseId = newExpense.id;
      const isNew = !expenseId;

      if (!expenseId) {
        expenseId = 'exp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      }

      // Determine Workflow Status
      let status: 'pending' | 'approved' | 'rejected' = 'approved';
      let submittedByUid = newExpense.submittedByUid || currentUser.uid;
      let submittedByName = newExpense.submittedByName || userProfile?.displayName || currentUser.displayName || currentUser.email?.split('@')[0] || '';
      let submittedByEmail = newExpense.submittedByEmail || currentUser.email || '';

      if (isStaffMember) {
        // Staff submissions are always pending
        status = 'pending';
      }

      const expenseData: Expense = {
        ...newExpense,
        id: expenseId,
        fileUrl,
        userId: currentUser.uid,
        status,
        submittedByUid,
        submittedByName,
        submittedByEmail,
        createdAt: isNew ? new Date().toISOString() : (newExpense.createdAt || new Date().toISOString()),
        updatedAt: new Date().toISOString()
      } as Expense;

      // Save directly to the cloud store
      await db.put('expenses', expenseData);

      // Trigger matching bank/cash book entry ONLY if approved (Bypasses for Staff submissions)
      if (status === 'approved' && isNew && newExpense.paymentMethod && newExpense.paymentMethod !== 'Cash') {
        const bankTransactionData = {
          id: 'btx_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
          date: newExpense.date,
          description: newExpense.title,
          type: 'debit',
          amount: newExpense.amount,
          category: newExpense.category,
          reference: newExpense.reference || '',
          bank: newExpense.paymentMethod,
          accountNumber: newExpense.accountNumber || '',
          chequeNumber: newExpense.chequeNumber || '',
          linkedEntityType: 'expense',
          linkedEntityId: expenseId,
          linkedEntityName: newExpense.title,
          userId: currentUser.uid,
          createdAt: Date.now()
        };

        await db.put('bankTransactions', bankTransactionData);
      }

      setSnackbar({
        open: true,
        message: `Expense ${isNew ? (isStaffMember ? 'submitted' : 'added') : 'updated'} successfully`,
        severity: 'success'
      });

      // Reset form
      setNewExpense({
        title: '',
        amount: 0,
        date: new Date().toISOString().split('T')[0],
        category: '',
        paymentMethod: '',
        reference: '',
        notes: '',
        fileUrl: '',
        accountNumber: '',
        chequeNumber: '',
      });
      setSelectedFile(null);
      setOpenExpenseDialog(false);

      // Refresh expenses
      fetchExpenses();
    } catch (error) {
      console.error('Error saving expense:', error);
      setSnackbar({
        open: true,
        message: 'Error saving expense',
        severity: 'error'
      });
    }
  };

  // Handle saving a recurring expense
  const handleSaveRecurring = async () => {
    if (!currentUser) return;

    // Validate required fields
    if (!newRecurring.title || !newRecurring.amount || !newRecurring.startDate || !newRecurring.frequency || !newRecurring.category || !newRecurring.paymentMethod) {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields',
        severity: 'error'
      });
      return;
    }

    try {
      const recurringData = {
        ...newRecurring,
        userId: currentUser.uid,
        updatedAt: new Date().toISOString(),
        createdAt: selectedRecurring ? selectedRecurring.createdAt : new Date().toISOString(),
      };

      let recurringId = selectedRecurring?.id;
      if (!recurringId) {
        recurringId = 'rec_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      }

      await db.put('recurringExpenses', {
        ...recurringData,
        id: recurringId
      });

      setSnackbar({
        open: true,
        message: `Recurring expense ${selectedRecurring ? 'updated' : 'added'} successfully`,
        severity: 'success'
      });

      handleCloseRecurringDialog();
      fetchRecurringExpenses();
    } catch (error) {
      console.error('Error saving recurring expense:', error);
      setSnackbar({
        open: true,
        message: 'Error saving recurring expense',
        severity: 'error'
      });
    }
  };

  // Handle approving an expense (Admin only)
  const handleApproveExpense = async (expense: Expense) => {
    if (!currentUser || !isAdminOrAccountant) return;

    // Smart Math Check
    const staffAdvances = advances.filter(a => a.staffUid === expense.submittedByUid && a.status === 'pending_settlement');
    const totalAdvances = staffAdvances.reduce((sum, a) => sum + a.amount, 0);
    
    let confirmMessage = `Are you sure you want to approve this expense for ${formatCurrency(expense.amount)}?`;
    if (expense.settlementMethod === 'against_advance' && totalAdvances > 0) {
      const balance = expense.amount - totalAdvances;
      confirmMessage = `Smart Math Summary:\nAdvance Given: ${formatCurrency(totalAdvances)}\nExpense Claimed: ${formatCurrency(expense.amount)}\nNet Balance: ${formatCurrency(balance)}\n\nApprove this expense?`;
    }

    if (!window.confirm(confirmMessage)) return;

    try {
      const updatedExpense: Expense = {
        ...expense,
        status: 'approved',
        payrollSettled: false,
        approvedByUid: currentUser.uid,
        approvedByName: userProfile?.displayName || currentUser.displayName || currentUser.email?.split('@')[0] || '',
        approvedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await db.put('expenses', updatedExpense);

      // Create matching bank transaction (if payment method is not Cash)
      if (expense.paymentMethod && expense.paymentMethod !== 'Cash') {
        const bankTransactionData = {
          id: 'btx_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
          date: expense.date,
          description: expense.title,
          type: 'debit',
          amount: expense.amount,
          category: expense.category,
          reference: expense.reference || '',
          bank: expense.paymentMethod,
          accountNumber: expense.accountNumber || '',
          chequeNumber: expense.chequeNumber || '',
          linkedEntityType: 'expense',
          linkedEntityId: expense.id,
          linkedEntityName: expense.title,
          userId: currentUser.uid,
          createdAt: Date.now()
        };

        await db.put('bankTransactions', bankTransactionData);
      }

      setSnackbar({
        open: true,
        message: 'Expense request approved successfully',
        severity: 'success'
      });

      fetchExpenses();
    } catch (error) {
      console.error('Error approving expense:', error);
      setSnackbar({
        open: true,
        message: 'Error approving expense',
        severity: 'error'
      });
    }
  };

  // Open rejection modal
  const handleOpenRejectionDialog = (expenseId: string) => {
    setRejectionExpenseId(expenseId);
    setRejectionReason('');
    setRejectionDialogOpen(true);
  };

  // Handle rejecting an expense (Admin only)
  const handleRejectExpense = async () => {
    if (!currentUser || !isAdminOrAccountant || !rejectionExpenseId) return;

    try {
      const expense = expenses.find(e => e.id === rejectionExpenseId);
      if (!expense) return;

      const updatedExpense: Expense = {
        ...expense,
        status: 'rejected',
        rejectionReason: rejectionReason.trim(),
        approvedByUid: currentUser.uid,
        approvedByName: userProfile?.displayName || currentUser.displayName || currentUser.email?.split('@')[0] || '',
        approvedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await db.put('expenses', updatedExpense);

      setSnackbar({
        open: true,
        message: 'Expense request rejected successfully',
        severity: 'success'
      });

      setRejectionDialogOpen(false);
      setRejectionExpenseId(null);
      setRejectionReason('');
      fetchExpenses();
    } catch (error) {
      console.error('Error rejecting expense:', error);
      setSnackbar({
        open: true,
        message: 'Error rejecting expense',
        severity: 'error'
      });
    }
  };

  // Handle deleting an expense
  const handleDeleteExpense = async (expenseId: string) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await db.remove('expenses', expenseId);
        setSnackbar({
          open: true,
          message: 'Expense deleted successfully',
          severity: 'success'
        });
        fetchExpenses();
      } catch (error) {
        console.error('Error deleting expense:', error);
        setSnackbar({
          open: true,
          message: 'Error deleting expense',
          severity: 'error'
        });
      }
    }
  };

  // Handle granting a staff advance
  const handleSaveAdvance = async () => {
    if (!currentUser || !isAdminOrAccountant) return;
    if (!newAdvance.staffUid || !newAdvance.amount || !newAdvance.reason || !newAdvance.paymentMethod) {
      setSnackbar({ open: true, message: 'Please fill all fields', severity: 'error' });
      return;
    }

    try {
      const selectedStaff = staffList.find(s => s.uid === newAdvance.staffUid);
      const advanceData: EmployeeAdvance = {
        id: 'adv_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
        tenantId: tenantId || currentUser.uid,
        staffUid: newAdvance.staffUid,
        staffName: selectedStaff ? selectedStaff.displayName : newAdvance.staffUid,
        staffEmail: selectedStaff ? selectedStaff.email : undefined,
        amount: newAdvance.amount,
        date: newAdvance.date,
        reason: newAdvance.reason,
        status: 'pending_settlement',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // 1. Save to Employee Advances
      await db.put('employee_advances', advanceData);

      // 2. Log Outward Flow in Cashbook / Bankbook
      if (newAdvance.paymentMethod) {
        const transactionData = {
          id: 'btx_adv_' + Date.now(),
          date: newAdvance.date,
          description: `Employee Advance: ${advanceData.staffName} - ${newAdvance.reason}`,
          type: 'debit',
          amount: newAdvance.amount,
          category: 'Employee Advances',
          reference: advanceData.id,
          bank: newAdvance.paymentMethod,
          linkedEntityType: 'advance',
          linkedEntityId: advanceData.id,
          linkedEntityName: advanceData.staffName,
          userId: currentUser.uid,
          createdAt: Date.now()
        };
        await db.put('bankTransactions', transactionData);
      }

      setSnackbar({ open: true, message: 'Advance granted successfully', severity: 'success' });
      setOpenAdvanceDialog(false);
      setNewAdvance({ staffUid: '', staffName: '', amount: 0, reason: '', date: new Date().toISOString().split('T')[0], paymentMethod: '' });
      fetchAdvances();
    } catch (error) {
      console.error('Error saving advance:', error);
      setSnackbar({ open: true, message: 'Error granting advance', severity: 'error' });
    }
  };

  // Handle staff opening the return dialog — full return only, fixed amount
  const handleOpenReturnDialog = (advance: EmployeeAdvance) => {
    setSelectedReturnAdvance(advance);
    const unrecovered = computeUnrecoveredBalance(advance);
    setReturnAmount(unrecovered);
    setReturnMethod('Cash');
    setReturnReference('');
    setOpenReturnDialog(true);
  };

  const handleInitiateReturnAdvance = async () => {
    if (!currentUser || !selectedReturnAdvance || returnAmount <= 0) return;
    if (returnMethod !== 'Cash') {
      if (!returnReference.trim()) {
        setSnackbar({ open: true, message: 'Please provide a transaction reference for UPI/Bank transfers', severity: 'error' });
        return;
      }
      if (!returnProofFile) {
        setSnackbar({ open: true, message: 'Please upload a transaction proof (receipt/screenshot)', severity: 'error' });
        return;
      }
    }
    // Block if a refund is already pending admin approval
    if (getPendingRefund(selectedReturnAdvance)) {
      setSnackbar({ open: true, message: 'A return request is already pending admin approval.', severity: 'error' });
      return;
    }

    try {
      let base64Proof = '';
      if (returnProofFile) {
        base64Proof = await convertFileToBase64(returnProofFile);
      }

      const newRefund: RefundRecord = {
        id: 'ref_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6),
        amount: returnAmount,
        method: returnMethod,
        reference: returnReference || undefined,
        proofRef: base64Proof || undefined,
        status: 'pending_admin',
        submittedAt: new Date().toISOString(),
      };
      const updatedAdvance = {
        ...selectedReturnAdvance,
        returnHistory: [...(selectedReturnAdvance.returnHistory || []), newRefund],
        updatedAt: new Date().toISOString(),
      };
      await db.put('employee_advances', updatedAdvance);
      setSnackbar({ open: true, message: 'Return request submitted with proof! Awaiting admin approval.', severity: 'success' });
      setOpenReturnDialog(false);
      setReturnMethod('Cash');
      setReturnReference('');
      setReturnProofFile(null);
      fetchAdvances();
    } catch (error) {
      console.error('Error submitting return request:', error);
      setSnackbar({ open: true, message: 'Error submitting return request', severity: 'error' });
    }
  };

  const handleApproveReturnAdvance = async (advance: EmployeeAdvance, refundId: string) => {
    if (!currentUser || !isAdminOrAccountant) return;
    const refund = (advance.returnHistory || []).find(r => r.id === refundId);
    if (!refund) return;

    if (!window.confirm(`Approve return of ${formatCurrency(refund.amount)} via ${refund.method}${refund.reference ? ` (Ref: ${refund.reference})` : ''}?`)) return;

    try {
      const updatedHistory = (advance.returnHistory || []).map(r =>
        r.id === refundId
          ? { ...r, status: 'approved' as const, approvedAt: new Date().toISOString(), approvedByName: userProfile?.displayName || currentUser.email || '' }
          : r
      );
      const unrecoveredAfter = computeUnrecoveredBalance({ ...advance, returnHistory: updatedHistory });
      const updatedAdvance = {
        ...advance,
        returnHistory: updatedHistory,
        status: unrecoveredAfter <= 0 ? 'settled' as const : 'pending_settlement' as const,
        updatedAt: new Date().toISOString(),
      };
      await db.put('employee_advances', updatedAdvance);

      // Auto-Settlement Sync: If the advance is now fully settled, mark its matched expenses as payrollSettled: true
      if (unrecoveredAfter <= 0) {
        const matchedExpenses = expenses.filter(e => {
          if (e.status !== 'approved' || e.payrollSettled === true) return false;
          // 1. UID match
          if (e.submittedByUid === advance.staffUid) return true;
          // 2. Email match
          if (e.submittedByEmail && advance.staffEmail && e.submittedByEmail.toLowerCase() === advance.staffEmail.toLowerCase()) return true;
          // 3. Normalized name match
          if (e.submittedByName && advance.staffName) {
            const eName = e.submittedByName.toLowerCase().replace(/\s+/g, '');
            const aName = advance.staffName.toLowerCase().replace(/\s+/g, '');
            if (eName === aName || eName.includes(aName) || aName.includes(eName)) return true;
          }
          return false;
        });

        for (const e of matchedExpenses) {
          await db.put('expenses', {
            ...e,
            payrollSettled: true,
            settledAt: new Date().toISOString()
          });
        }
      }

      // Ledger: credit to cashTransactions (Cash) or bankTransactions (UPI/Bank)
      const txData = {
        id: 'btx_ret_' + Date.now(),
        date: new Date().toISOString().split('T')[0],
        description: `Advance Refund: ${advance.staffName} (Ref: ${refund.reference || 'Cash'})`,
        type: 'credit',
        amount: refund.amount,
        category: 'Advance Refund',
        reference: advance.id,
        bank: refund.method,
        linkedEntityType: 'advance_return',
        linkedEntityId: advance.id,
        linkedEntityName: advance.staffName,
        userId: currentUser.uid,
        createdAt: Date.now(),
      };
      if (refund.method === 'Cash') {
        await db.put('cashTransactions', { ...txData, balance: 0 });
      } else {
        await db.put('bankTransactions', txData);
      }

      setSnackbar({ open: true, message: `✅ Approved ${formatCurrency(refund.amount)} return from ${advance.staffName}. Ledger updated.`, severity: 'success' });
      fetchAdvances();
    } catch (error) {
      console.error('Error approving return:', error);
      setSnackbar({ open: true, message: 'Error approving return', severity: 'error' });
    }
  };

  const handleRejectReturnAdvance = async (advance: EmployeeAdvance, refundId: string) => {
    if (!currentUser || !isAdminOrAccountant) return;
    const refund = (advance.returnHistory || []).find(r => r.id === refundId);
    if (!refund) return;
    if (!window.confirm(`Reject this return request of ${formatCurrency(refund.amount)} via ${refund.method}? The staff member will be able to re-submit.`)) return;

    try {
      const updatedHistory = (advance.returnHistory || []).map(r =>
        r.id === refundId ? { ...r, status: 'rejected' as const } : r
      );
      await db.put('employee_advances', { ...advance, returnHistory: updatedHistory, updatedAt: new Date().toISOString() });
      setSnackbar({ open: true, message: 'Return request rejected. Staff can now re-submit.', severity: 'success' });
      fetchAdvances();
    } catch (error) {
      console.error('Error rejecting return:', error);
      setSnackbar({ open: true, message: 'Error rejecting return', severity: 'error' });
    }
  };
  // Handle deleting a recurring expense
  const handleDeleteRecurring = async (recurringId: string) => {
    if (window.confirm('Are you sure you want to delete this recurring expense?')) {
      try {
        await db.remove('recurringExpenses', recurringId);
        setSnackbar({
          open: true,
          message: 'Recurring expense deleted successfully',
          severity: 'success'
        });
        fetchRecurringExpenses();
      } catch (error) {
        console.error('Error deleting recurring expense:', error);
        setSnackbar({
          open: true,
          message: 'Error deleting recurring expense',
          severity: 'error'
        });
      }
    }
  };

  // Handle close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <AnimatedPage>
      {/* Micro-animations CSS block */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.1); opacity: 0.7; }
            100% { transform: scale(1); opacity: 1; }
          }
        `}
      </style>

      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" sx={{
            background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold'
          }}>
            Expenses Management
          </Typography>

          <Box display="flex" alignItems="center">
            {/* Real-time Refresh Button */}
            <Tooltip title="Refresh Data (Automatic background updates every 30s)">
              <IconButton
                color="primary"
                onClick={async () => {
                  setLoading(true);
                  await fetchExpenses();
                  await fetchRecurringExpenses();
                  setLoading(false);
                }}
                disabled={loading}
                sx={{ mr: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              >
                <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
              </IconButton>
            </Tooltip>

            <IconButton
              color="primary"
              onClick={() => {
                const dialog = document.createElement('dialog');
                dialog.setAttribute('style', 'border: none; border-radius: 8px; padding: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.1); width: 300px;');
                dialog.innerHTML = `
                  <div style="display: flex; flex-direction: column;">
                    <h3 style="margin-top: 0; color: #4CAF50;">Search Expenses</h3>
                    <input 
                      type="text" 
                      value="${searchQuery}" 
                      placeholder="Enter search terms..."
                      style="padding: 8px; border: 1px solid #ddd; border-radius: 4px; margin-bottom: 16px;"
                      id="searchInput"
                    />
                    <div style="display: flex; justify-content: flex-end; gap: 8px;">
                      <button 
                        style="padding: 8px 16px; border: none; border-radius: 4px; background: #f5f5f5; cursor: pointer;"
                        onclick="this.closest('dialog').close(); document.getElementById('clearSearch').click();"
                      >
                        Clear
                      </button>
                      <button 
                        style="padding: 8px 16px; border: none; border-radius: 4px; background: #4CAF50; color: white; cursor: pointer;"
                        onclick="document.getElementById('applySearch').click(); this.closest('dialog').close();"
                      >
                        Search
                      </button>
                    </div>
                  </div>
                `;
                document.body.appendChild(dialog);
                const searchInput = dialog.querySelector('#searchInput') as HTMLInputElement;
                searchInput.addEventListener('keyup', (e) => {
                  if (e.key === 'Enter') {
                    setSearchQuery(searchInput.value);
                    dialog.close();
                  }
                });
                dialog.addEventListener('close', () => {
                  document.body.removeChild(dialog);
                });
                dialog.showModal();
              }}
              sx={{ mr: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
            >
              <SearchIcon />
            </IconButton>

            <button id="applySearch" style={{ display: 'none' }} onClick={(e) => {
              const searchInput = document.getElementById('searchInput') as HTMLInputElement;
              if (searchInput) {
                setSearchQuery(searchInput.value);
              }
            }}></button>

            <button id="clearSearch" style={{ display: 'none' }} onClick={(e) => {
              setSearchQuery('');
            }}></button>

            <Box display="flex" alignItems="center" gap={1.5} flexWrap="nowrap">
              {isAdminOrAccountant && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<AttachMoneyIcon />}
                    onClick={() => setOpenAdvanceDialog(true)}
                    sx={{ color: theme.palette.info.main, borderColor: theme.palette.info.main, whiteSpace: 'nowrap' }}
                  >
                    Grant Advance
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RepeatIcon />}
                    onClick={() => setOpenRecurringDialog(true)}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Add Recurring
                  </Button>
                </>
              )}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenExpenseDialog()}
                sx={{
                  background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)',
                  color: 'white',
                  fontWeight: 'medium',
                  whiteSpace: 'nowrap',
                }}
              >
                {isStaffMember ? 'Raise Expense' : 'Add Expense'}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Summary Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(139, 195, 74, 0.05) 100%)',
                    border: '1px solid rgba(76, 175, 80, 0.1)',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    '&:hover': {
                      boxShadow: '0 8px 25px rgba(76, 175, 80, 0.15)',
                      transform: 'translateY(-5px)',
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(76, 175, 80, 0.2)',
                        color: '#4CAF50',
                        mr: 2
                      }}
                    >
                      <AccountBalanceWalletIcon fontSize="small" />
                    </Box>
                    <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                      {isStaffMember ? 'My Approved Claims' : 'Total Approved Expenses'}
                    </Typography>
                  </Box>

                  <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#4CAF50' }}>
                    {formatCurrency(totalExpenses)}
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, rgba(139, 195, 74, 0.15) 0%, rgba(205, 220, 57, 0.05) 100%)',
                    border: '1px solid rgba(139, 195, 74, 0.1)',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    '&:hover': {
                      boxShadow: '0 8px 25px rgba(139, 195, 74, 0.15)',
                      transform: 'translateY(-5px)',
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(139, 195, 74, 0.2)',
                        color: '#8BC34A',
                        mr: 2
                      }}
                    >
                      <AttachMoneyIcon fontSize="small" />
                    </Box>
                    <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                      Monthly Claims
                    </Typography>
                  </Box>

                  <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#8BC34A' }}>
                    {formatCurrency(monthlyExpenses)}
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, rgba(205, 220, 57, 0.15) 0%, rgba(255, 235, 59, 0.05) 100%)',
                    border: '1px solid rgba(205, 220, 57, 0.1)',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    '&:hover': {
                      boxShadow: '0 8px 25px rgba(205, 220, 57, 0.15)',
                      transform: 'translateY(-5px)',
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(205, 220, 57, 0.2)',
                        color: '#CDDC39',
                        mr: 2
                      }}
                    >
                      <ReceiptIcon fontSize="small" />
                    </Box>
                    <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                      {isStaffMember ? 'My Submissions' : 'Total Expense Count'}
                    </Typography>
                  </Box>

                  <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#CDDC39' }}>
                    {expenses.length}
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, rgba(255, 235, 59, 0.15) 0%, rgba(255, 193, 7, 0.05) 100%)',
                    border: '1px solid rgba(255, 235, 59, 0.1)',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    '&:hover': {
                      boxShadow: '0 8px 25px rgba(255, 235, 59, 0.15)',
                      transform: 'translateY(-5px)',
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 235, 59, 0.2)',
                        color: '#FFC107',
                        mr: 2
                      }}
                    >
                      <RepeatIcon fontSize="small" />
                    </Box>
                    <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                      Pending Approvals
                    </Typography>
                  </Box>

                  <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#FFC107' }}>
                    {expenses.filter(e => e.status === 'pending').length}
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>

        {/* ── Unrecovered Advance Warning Banner (Staff Only) — Lifted above tabs for global visibility */}
        {isStaffMember && (() => {
          const pendingRefundAdvance = advances.find(a => getPendingRefund(a));
          const rejectedRefundAdvance = advances.find(a => !getPendingRefund(a) && getLatestRejectedRefund(a));
          const rejectedRefund = rejectedRefundAdvance ? getLatestRejectedRefund(rejectedRefundAdvance) : undefined;

          if (pendingRefundAdvance) {
            const pRefund = getPendingRefund(pendingRefundAdvance)!;
            return (
              <Box sx={{ mb: 3, p: 2, borderRadius: 2, border: '2px solid', borderColor: 'info.main', bgcolor: 'info.light', display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ fontSize: '1.8rem' }}>⏳</Box>
                <Box>
                  <Typography variant="body1" fontWeight="bold" color="info.dark">
                    Refund of {formatCurrency(pRefund.amount)} via {pRefund.method} is pending Admin verification.
                  </Typography>
                  <Typography variant="body2" color="info.dark">You’ll be notified once the admin approves your return.</Typography>
                </Box>
              </Box>
            );
          }

          if (rejectedRefund && myUnspentBalance > 0) {
            return (
              <Box sx={{ mb: 3, p: 2, borderRadius: 2, border: '2px solid', borderColor: 'error.main', bgcolor: 'error.light', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box sx={{ fontSize: '1.8rem' }}>❌</Box>
                  <Box>
                    <Typography variant="body1" fontWeight="bold" color="error.dark">Your return request was rejected.</Typography>
                    <Typography variant="body2" color="error.dark">
                      {rejectedRefund.reference ? `Ref “${rejectedRefund.reference}” could not be verified.` : 'Please re-submit with a valid proof.'}
                      {' '}Unspent balance: <strong>{formatCurrency(myUnspentBalance)}</strong>
                    </Typography>
                  </Box>
                </Box>
                <Button variant="contained" color="error" size="small" sx={{ whiteSpace: 'nowrap', fontWeight: 'bold', color: 'white' }}
                  onClick={() => { const adv = advances.find(a => a.status === 'pending_settlement'); if (adv) handleOpenReturnDialog(adv); }}>
                  Re-submit Return
                </Button>
              </Box>
            );
          }

          if (myUnspentBalance > 0) {
            return (
              <Box sx={{ mb: 3, p: 2, borderRadius: 2, border: '2px solid', borderColor: 'warning.main', bgcolor: 'warning.light', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box sx={{ fontSize: '2rem' }}>⚠️</Box>
                  <Box>
                    <Typography variant="body1" fontWeight="bold" color="warning.dark">
                      You have an unspent advance balance of {formatCurrency(myUnspentBalance)}
                    </Typography>
                    <Typography variant="body2" color="warning.dark">
                      Please return this amount, or it will be <strong>deducted from your next salary</strong>.
                    </Typography>
                  </Box>
                </Box>
                <Button variant="contained" color="warning" size="small" sx={{ whiteSpace: 'nowrap', fontWeight: 'bold', color: 'white' }}
                  onClick={() => { const adv = advances.find(a => a.status === 'pending_settlement' && !getPendingRefund(a)); if (adv) handleOpenReturnDialog(adv); }}>
                  Return Balance
                </Button>
              </Box>
            );
          }
          return null;
        })()}

        {/* Tabs and Actions */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab 
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>All Expenses</span>
                    {expenses.filter(e => e.status === 'pending').length > 0 && (
                      <Chip 
                        label={expenses.filter(e => e.status === 'pending').length} 
                        size="small" 
                        color="warning" 
                        sx={{ height: 18, fontSize: 10, fontWeight: 'bold' }} 
                      />
                    )}
                  </Box>
                } 
              />
              <Tab label="Recurring Expenses" />
              <Tab label="Staff Advances" />
            </Tabs>

            <Box display="flex" alignItems="center">
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => { }}
                size="small"
                sx={{ mr: 2 }}
              >
                Filter
              </Button>

              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={exportToCSV}
                size="small"
              >
                Export
              </Button>
            </Box>
          </Box>

          {/* Status Filter Chips (Sub-filter bar) */}
          {tabValue === 0 && (
            <Box display="flex" gap={1} mt={2}>
              <Chip
                label={`All (${expenses.length})`}
                onClick={() => setStatusFilter('all')}
                variant={statusFilter === 'all' ? 'filled' : 'outlined'}
                color="primary"
                size="small"
                sx={{ fontWeight: 'medium' }}
              />
              <Chip
                label={`Pending (${expenses.filter(e => e.status === 'pending').length})`}
                onClick={() => setStatusFilter('pending')}
                variant={statusFilter === 'pending' ? 'filled' : 'outlined'}
                color="warning"
                size="small"
                sx={{ 
                  fontWeight: 'medium',
                  '& .MuiChip-label': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }
                }}
                icon={
                  expenses.filter(e => e.status === 'pending').length > 0 ? (
                    <Box 
                      sx={{ 
                        width: 6, 
                        height: 6, 
                        borderRadius: '50%', 
                        bgcolor: 'white', 
                        animation: 'pulse 1.5s infinite' 
                      }} 
                    />
                  ) : undefined
                }
              />
              <Chip
                label={`Approved (${expenses.filter(e => !e.status || e.status === 'approved').length})`}
                onClick={() => setStatusFilter('approved')}
                variant={statusFilter === 'approved' ? 'filled' : 'outlined'}
                color="success"
                size="small"
                sx={{ fontWeight: 'medium' }}
              />
              <Chip
                label={`Rejected (${expenses.filter(e => e.status === 'rejected').length})`}
                onClick={() => setStatusFilter('rejected')}
                variant={statusFilter === 'rejected' ? 'filled' : 'outlined'}
                color="error"
                size="small"
                sx={{ fontWeight: 'medium' }}
              />
            </Box>
          )}
        </Box>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{
              borderRadius: 2,
              boxShadow: '0 4px 20px 0 rgba(0,0,0,0.14)',
              overflowX: 'auto',
              width: '100%'
            }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)' }}>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'date'}
                        direction={sortConfig.key === 'date' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('date')}
                        sx={{ color: 'white', '&.MuiTableSortLabel-active': { color: 'white' } }}
                      >
                        Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'title'}
                        direction={sortConfig.key === 'title' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('title')}
                        sx={{ color: 'white', '&.MuiTableSortLabel-active': { color: 'white' } }}
                      >
                        Expense
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'category'}
                        direction={sortConfig.key === 'category' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('category')}
                        sx={{ color: 'white', '&.MuiTableSortLabel-active': { color: 'white' } }}
                      >
                        Category
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'amount'}
                        direction={sortConfig.key === 'amount' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('amount')}
                        sx={{ color: 'white', '&.MuiTableSortLabel-active': { color: 'white' } }}
                      >
                        Amount
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Payment Method</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Reference</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Submitted By</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getSortedExpenses().map((expense) => {
                    const isPending = expense.status === 'pending';
                    const isApproved = !expense.status || expense.status === 'approved';
                    const isRejected = expense.status === 'rejected';

                    return (
                      <TableRow key={expense.id} hover>
                        <TableCell>{expense.date ? format(new Date(expense.date), 'dd MMM yyyy') : '-'}</TableCell>
                        <TableCell>{expense.title}</TableCell>
                        <TableCell>
                          <Chip
                            label={expense.category}
                            size="small"
                            sx={{
                              backgroundColor: alpha(theme.palette.success.light, 0.1),
                              color: theme.palette.success.dark,
                              fontWeight: 'medium'
                            }}
                          />
                        </TableCell>
                        <TableCell>{formatCurrency(expense.amount)}</TableCell>
                        <TableCell>{expense.paymentMethod}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{expense.reference || '-'}</Typography>
                          {expense.fileUrl && (
                            <Box sx={{ mt: 0.5 }}>
                              <span
                                onClick={() => setPreviewImage(expense.fileUrl || null)}
                                style={{ 
                                  color: theme.palette.secondary.main, 
                                  fontWeight: 'bold', 
                                  textDecoration: 'underline', 
                                  cursor: 'pointer',
                                  fontSize: '0.75rem',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '2px'
                                }}
                              >
                                View Receipt 📎
                              </span>
                            </Box>
                          )}
                        </TableCell>
                        
                        {/* Status Chip with rich tooltips */}
                        <TableCell>
                          {isPending && (
                            <Chip 
                              label="Pending Approval" 
                              color="warning" 
                              size="small"
                              sx={{ 
                                animation: 'pulse 2s infinite',
                                fontWeight: 'bold'
                              }}
                            />
                          )}
                          {isApproved && (
                            <Tooltip title={expense.approvedByName ? `Approved by ${expense.approvedByName} on ${expense.approvedAt ? format(new Date(expense.approvedAt), 'dd MMM yyyy') : '-'}` : 'Automatically Approved'}>
                              <Chip 
                                label="Approved" 
                                color="success" 
                                size="small"
                                sx={{ fontWeight: 'bold' }}
                              />
                            </Tooltip>
                          )}
                          {isRejected && (
                            <Tooltip title={`Rejection Reason: ${expense.rejectionReason || 'No reason provided'}`}>
                              <Chip 
                                label="Rejected" 
                                color="error" 
                                size="small"
                                sx={{ fontWeight: 'bold' }}
                                icon={<FeedbackIcon style={{ fontSize: 14 }} />}
                              />
                            </Tooltip>
                          )}
                        </TableCell>

                        {/* Submitted By Column */}
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {expense.submittedByName || 'Admin'}
                          </Typography>
                          {expense.submittedByEmail && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {expense.submittedByEmail}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Lock / Unlock Conditional Action buttons */}
                        <TableCell>
                          <Box display="flex" gap={1} alignItems="center">
                            {/* Admin Approve/Reject buttons */}
                            {isAdminOrAccountant && isPending && (
                              <>
                                <Tooltip title="Approve Request">
                                  <IconButton 
                                    size="small" 
                                    color="success" 
                                    onClick={() => handleApproveExpense(expense)}
                                    sx={{ border: '1px solid', borderColor: 'success.light', bgcolor: alpha(theme.palette.success.light, 0.05) }}
                                  >
                                    <ApproveIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Reject Request">
                                  <IconButton 
                                    size="small" 
                                    color="error" 
                                    onClick={() => handleOpenRejectionDialog(expense.id)}
                                    sx={{ border: '1px solid', borderColor: 'error.light', bgcolor: alpha(theme.palette.error.light, 0.05) }}
                                  >
                                    <RejectIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}

                            {/* Edit / Delete locking rules */}
                            {isAdminOrAccountant ? (
                              // Admins have full rights to edit and delete
                              <>
                                <IconButton size="small" onClick={() => handleOpenExpenseDialog(expense)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleDeleteExpense(expense.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </>
                            ) : (
                              // Staff lock: can delete only when pending, cannot edit at all
                              <>
                                {isPending ? (
                                  <Tooltip title="Cancel Request">
                                    <IconButton size="small" onClick={() => handleDeleteExpense(expense.id)}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Reviewed expenses are locked and cannot be modified">
                                    <LockIcon fontSize="small" color="disabled" />
                                  </Tooltip>
                                )}
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {getSortedExpenses().length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                        <Typography variant="subtitle1" color="text.secondary">
                          No expenses found matching your criteria
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{
              borderRadius: 2,
              boxShadow: '0 4px 20px 0 rgba(0,0,0,0.14)',
              overflowX: 'auto',
              width: '100%'
            }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)' }}>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Title</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Amount</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Frequency</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Category</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Start Date</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>End Date</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recurringExpenses.map((recurring) => (
                    <TableRow key={recurring.id} hover>
                      <TableCell>{recurring.title}</TableCell>
                      <TableCell>{formatCurrency(recurring.amount)}</TableCell>
                      <TableCell>
                        <Chip
                          label={recurring.frequency.charAt(0).toUpperCase() + recurring.frequency.slice(1)}
                          size="small"
                          sx={{
                            backgroundColor: alpha(theme.palette.info.light, 0.1),
                            color: theme.palette.info.dark,
                            fontWeight: 'medium'
                          }}
                        />
                      </TableCell>
                      <TableCell>{recurring.category}</TableCell>
                      <TableCell>{recurring.startDate ? format(new Date(recurring.startDate), 'dd MMM yyyy') : '-'}</TableCell>
                      <TableCell>{recurring.endDate ? format(new Date(recurring.endDate), 'dd MMM yyyy') : '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={recurring.isActive ? 'Active' : 'Inactive'}
                          color={recurring.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <IconButton size="small" onClick={() => handleOpenRecurringDialog(recurring)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDeleteRecurring(recurring.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {recurringExpenses.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                        <Typography variant="subtitle1" color="text.secondary">
                          No recurring expenses found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Admin: Pending Advance Returns */}
          {isAdminOrAccountant && (() => {
            const pendingReturns = advances.flatMap(a =>
              (a.returnHistory || [])
                .filter(r => r.status === 'pending_admin')
                .map(r => ({ advance: a, refund: r }))
            );
            return pendingReturns.length > 0 ? (
              <Box sx={{ mb: 3, p: 2.5, borderRadius: 2, border: '2px solid', borderColor: 'warning.main', bgcolor: alpha(theme.palette.warning.main, 0.06) }}>
                <Typography variant="subtitle2" fontWeight="bold" color="warning.dark" mb={1.5}>
                  ⏳ {pendingReturns.length} Pending Advance Return{pendingReturns.length > 1 ? 's' : ''} — Awaiting Your Review
                </Typography>
                {pendingReturns.map(({ advance, refund }) => (
                  <Box key={refund.id} sx={{ p: 1.5, mb: 1, borderRadius: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, fontSize: '0.875rem', fontWeight: 'bold' }}>
                      {advance.staffName.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" fontWeight="bold">{advance.staffName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatCurrency(refund.amount)} via {refund.method}
                        {refund.reference && ` · Ref: ${refund.reference}`}
                        {' · '}{new Date(refund.submittedAt).toLocaleDateString()}
                        {refund.proofRef && (
                          <>
                            {' · '}
                            <span
                              onClick={() => setPreviewImage(refund.proofRef || null)}
                              style={{ color: theme.palette.secondary.main, fontWeight: 'bold', textDecoration: 'underline', cursor: 'pointer' }}
                            >
                              View Proof Receipt 📎
                            </span>
                          </>
                        )}
                      </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                      <Button size="small" variant="contained" color="success" onClick={() => handleApproveReturnAdvance(advance, refund.id)}>
                        ✓ Approve Receipt
                      </Button>
                      <Button size="small" variant="outlined" color="error" onClick={() => handleRejectReturnAdvance(advance, refund.id)}>
                        ✗ Reject
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : null;
          })()}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.14)', overflowX: 'auto', width: '100%' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)' }}>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Staff Member</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Reason</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Advance Amount</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Unrecovered Balance</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Status / Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {advances.map((advance) => {
                    const unrecovered = computeUnrecoveredBalance(advance);
                    const pendingRefund = getPendingRefund(advance);
                    const rejectedRefund = !pendingRefund ? getLatestRejectedRefund(advance) : undefined;
                    return (
                      <TableRow key={advance.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, fontSize: '0.875rem', fontWeight: 'bold' }}>
                              {advance.staffName.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" fontWeight="500">{advance.staffName}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{new Date(advance.date).toLocaleDateString()}</TableCell>
                        <TableCell>{advance.reason}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">{formatCurrency(advance.amount)}</Typography>
                        </TableCell>
                        <TableCell>
                          {advance.status === 'settled' ? (
                            <Chip label="Fully Settled" color="success" size="small" />
                          ) : unrecovered > 0 ? (
                            <Typography variant="body2" fontWeight="bold" color="error.main">{formatCurrency(unrecovered)}</Typography>
                          ) : (
                            <Chip label="Covered by Expenses" color="success" size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" flexDirection="column" gap={1} alignItems="flex-start">
                            <Chip label={advance.status === 'settled' ? 'Settled' : 'Active'} color={advance.status === 'settled' ? 'success' : 'warning'} size="small" />
                            {/* Staff: pending approval */}
                            {isStaffMember && pendingRefund && (
                              <Chip label={`⏳ ${formatCurrency(pendingRefund.amount)} return pending approval`} color="info" size="small" />
                            )}
                            {/* Staff: rejected — re-enable button */}
                            {isStaffMember && rejectedRefund && advance.status === 'pending_settlement' && unrecovered > 0 && (
                              <Box>
                                <Typography variant="caption" color="error.main" display="block" mb={0.5}>
                                  ❌ Return rejected{rejectedRefund.reference ? ` (Ref: ${rejectedRefund.reference})` : ''}
                                </Typography>
                                <Button size="small" variant="contained" color="error" onClick={() => handleOpenReturnDialog(advance)}>
                                  Re-submit Return
                                </Button>
                              </Box>
                            )}
                            {/* Staff: no pending, no rejection — show return button */}
                            {isStaffMember && advance.status === 'pending_settlement' && !pendingRefund && !rejectedRefund && unrecovered > 0 && (
                              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                Use top banner to return
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {advances.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        <Typography variant="subtitle1" color="text.secondary">No employee advances found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>


        {/* Regular Expense Dialog */}
        <Dialog open={openExpenseDialog} onClose={handleCloseExpenseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {selectedExpense ? 'Edit Expense' : (isStaffMember ? 'Raise New Expense Request' : 'Add New Expense')}
          </DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={2} p={1}>
              {isStaffMember && activeAdvanceBalance > 0 && (
                <Alert severity="info" icon={<AttachMoneyIcon />}>
                  <Typography variant="body2" fontWeight="bold">Active Advance Balance: {formatCurrency(activeAdvanceBalance)}</Typography>
                  <Typography variant="caption">You have an unsettled advance. Consider submitting this expense against it.</Typography>
                </Alert>
              )}
              {isStaffMember && (
                <FormControl fullWidth required>
                  <InputLabel>Settlement Method</InputLabel>
                  <Select
                    name="settlementMethod"
                    value={newExpense.settlementMethod || 'out_of_pocket'}
                    label="Settlement Method"
                    onChange={handleExpenseSelectChange}
                  >
                    <MenuItem value="against_advance">Claim against active advance</MenuItem>
                    <MenuItem value="out_of_pocket">Out of pocket reimbursement</MenuItem>
                  </Select>
                </FormControl>
              )}
              <TextField
                label="Title"
                name="title"
                value={newExpense.title}
                onChange={handleExpenseInputChange}
                fullWidth
                required
              />
              <TextField
                label="Amount"
                name="amount"
                type="number"
                value={newExpense.amount === 0 ? '' : newExpense.amount}
                onChange={handleExpenseInputChange}
                fullWidth
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
              <DatePicker
                label="Date"
                value={newExpense.date ? new Date(newExpense.date) : null}
                onChange={handleExpenseDateChange}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={newExpense.category}
                  label="Category"
                  onChange={handleExpenseSelectChange}
                >
                  {EXPENSE_CATEGORIES.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  name="paymentMethod"
                  value={newExpense.paymentMethod}
                  label="Payment Method"
                  onChange={handleExpenseSelectChange}
                >
                  {PAYMENT_METHODS.map((method) => (
                    <MenuItem key={method} value={method}>
                      {method}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Reference Number"
                name="reference"
                value={newExpense.reference}
                onChange={handleExpenseInputChange}
                fullWidth
              />
              <TextField
                label="Notes"
                name="notes"
                value={newExpense.notes}
                onChange={handleExpenseInputChange}
                fullWidth
                multiline
                rows={3}
              />
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Attachment
                </Typography>
                <input
                  type="file"
                  onChange={handleFileSelect}
                  style={{ marginBottom: '10px' }}
                />
                {selectedFile && (
                  <Box mt={1}>
                    <Typography variant="body2">
                      Selected file: {selectedFile.name}
                    </Typography>
                  </Box>
                )}
                {newExpense.fileUrl && !selectedFile && (
                  <Box mt={1} display="flex" alignItems="center">
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      Current attachment:
                    </Typography>
                    <Button
                      component="a"
                      href={newExpense.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<ReceiptIcon />}
                      size="small"
                      variant="outlined"
                    >
                      View
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseExpenseDialog}>Cancel</Button>
            <Button
              onClick={handleSaveExpense}
              variant="contained"
              sx={{
                backgroundImage: 'var(--primary-gradient)',
              }}
            >
              {isStaffMember ? 'Submit Request' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Recurring Expense Dialog */}
        <Dialog open={openRecurringDialog} onClose={handleCloseRecurringDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {selectedRecurring ? 'Edit Recurring Expense' : 'Add New Recurring Expense'}
          </DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={2} p={1}>
              <TextField
                label="Title"
                name="title"
                value={newRecurring.title}
                onChange={handleRecurringInputChange}
                fullWidth
                required
              />
              <TextField
                label="Amount"
                name="amount"
                type="number"
                value={newRecurring.amount === 0 ? '' : newRecurring.amount}
                onChange={handleRecurringInputChange}
                fullWidth
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
              <FormControl fullWidth required>
                <InputLabel>Frequency</InputLabel>
                <Select
                  name="frequency"
                  value={newRecurring.frequency}
                  label="Frequency"
                  onChange={handleRecurringSelectChange}
                >
                  {FREQUENCIES.map((frequency) => (
                    <MenuItem key={frequency.value} value={frequency.value}>
                      {frequency.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <DatePicker
                label="Start Date"
                value={newRecurring.startDate ? new Date(newRecurring.startDate) : null}
                onChange={handleRecurringStartDateChange}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
              <DatePicker
                label="End Date (Optional)"
                value={newRecurring.endDate ? new Date(newRecurring.endDate) : null}
                onChange={handleRecurringEndDateChange}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={newRecurring.category}
                  label="Category"
                  onChange={handleRecurringSelectChange}
                >
                  {EXPENSE_CATEGORIES.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  name="paymentMethod"
                  value={newRecurring.paymentMethod}
                  label="Payment Method"
                  onChange={handleRecurringSelectChange}
                >
                  {PAYMENT_METHODS.map((method) => (
                    <MenuItem key={method} value={method}>
                      {method}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Notes"
                name="notes"
                value={newRecurring.notes}
                onChange={handleRecurringInputChange}
                fullWidth
                multiline
                rows={3}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={newRecurring.isActive || false}
                    onChange={handleRecurringSwitchChange}
                    color="primary"
                  />
                }
                label="Active"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRecurringDialog}>Cancel</Button>
            <Button
              onClick={handleSaveRecurring}
              variant="contained"
              sx={{
                backgroundImage: 'var(--primary-gradient)',
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Grant Advance Dialog */}
        <Dialog open={openAdvanceDialog} onClose={() => setOpenAdvanceDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ color: theme.palette.info.main, fontWeight: 'bold' }}>
            Grant Employee Advance
          </DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={2} p={1}>
              <FormControl fullWidth required>
                <InputLabel>Staff Member</InputLabel>
                <Select
                  value={newAdvance.staffUid}
                  label="Staff Member"
                  onChange={(e) => setNewAdvance({ ...newAdvance, staffUid: e.target.value as string })}
                >
                  {staffList.map((staff) => (
                    <MenuItem key={staff.uid} value={staff.uid}>
                      {staff.displayName} ({staff.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Amount"
                type="number"
                value={newAdvance.amount === 0 ? '' : newAdvance.amount}
                onChange={(e) => setNewAdvance({ ...newAdvance, amount: parseFloat(e.target.value) || 0 })}
                fullWidth
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
              <DatePicker
                label="Disbursement Date"
                value={newAdvance.date ? new Date(newAdvance.date) : null}
                onChange={(date) => setNewAdvance({ ...newAdvance, date: date ? date.toISOString().split('T')[0] : '' })}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
              <TextField
                label="Purpose / Reason"
                value={newAdvance.reason}
                onChange={(e) => setNewAdvance({ ...newAdvance, reason: e.target.value })}
                fullWidth
                required
              />
              <FormControl fullWidth required>
                <InputLabel>Paid From (Cashbook/Bank)</InputLabel>
                <Select
                  value={newAdvance.paymentMethod}
                  label="Paid From (Cashbook/Bank)"
                  onChange={(e) => setNewAdvance({ ...newAdvance, paymentMethod: e.target.value as string })}
                >
                  {PAYMENT_METHODS.map((method) => (
                    <MenuItem key={method} value={method}>
                      {method}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAdvanceDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSaveAdvance}
              variant="contained"
              sx={{ bgcolor: theme.palette.info.main, '&:hover': { bgcolor: theme.palette.info.dark } }}
            >
              Grant Advance
            </Button>
          </DialogActions>
        </Dialog>

        {/* Rejection Reason Dialog Modal */}
        <Dialog open={rejectionDialogOpen} onClose={() => setRejectionDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ color: theme.palette.error.main, fontWeight: 'bold' }}>
            Reject Expense Request
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" paragraph>
              Please provide a brief reason for rejecting this expense request. This reason will be visible to the submitting staff member.
            </Typography>
            <TextField
              label="Rejection Reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              fullWidth
              required
              multiline
              rows={3}
              placeholder="e.g., Missing valid tax invoice attachment, or incorrect billing category."
              autoFocus
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectionDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleRejectExpense}
              variant="contained"
              color="error"
              disabled={!rejectionReason.trim()}
            >
              Confirm Reject
            </Button>
          </DialogActions>
        </Dialog>

        {/* Staff: Settle Balance Dialog */}
        <Dialog
          open={openReturnDialog}
          onClose={() => { setOpenReturnDialog(false); setReturnMethod('Cash'); setReturnReference(''); setReturnProofFile(null); }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            💸 Settle Your Advance Balance
          </DialogTitle>
          <DialogContent dividers>
            {/* Warning Banner */}
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                Outstanding Balance: {formatCurrency(returnAmount)}
              </Typography>
              <Typography variant="caption">
                If not returned by end of month, this amount will be deducted from your next salary.
              </Typography>
            </Alert>

            <Box display="flex" flexDirection="column" gap={2.5} pt={1}>
              {/* Amount */}
              <TextField
                label="Amount You Are Returning (Fixed Full Return)"
                type="number"
                value={returnAmount === 0 ? '' : returnAmount}
                fullWidth
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  readOnly: true,
                }}
                helperText="Only full return of outstanding advance balance is allowed."
              />

              {/* Payment Method */}
              <FormControl fullWidth required>
                <InputLabel>How did you send the money?</InputLabel>
                <Select
                  value={returnMethod}
                  label="How did you send the money?"
                  onChange={(e) => { setReturnMethod(e.target.value as 'Cash' | 'UPI' | 'Bank'); setReturnReference(''); setReturnProofFile(null); }}
                >
                  <MenuItem value="Cash">💵 Cash in Hand</MenuItem>
                  <MenuItem value="UPI">📱 UPI Transfer</MenuItem>
                  <MenuItem value="Bank">🏦 Bank Transfer / NEFT</MenuItem>
                </Select>
              </FormControl>

              {/* Reference — required for UPI/Bank */}
              {returnMethod !== 'Cash' && (
                <TextField
                  label={returnMethod === 'UPI' ? 'UPI Transaction Reference / UTR *' : 'Bank Transaction Reference / UTR *'}
                  value={returnReference}
                  onChange={(e) => setReturnReference(e.target.value)}
                  fullWidth
                  required
                  placeholder={returnMethod === 'UPI' ? 'e.g. 123456789012' : 'e.g. NEFT/IMPS UTR number'}
                  helperText="This reference will be verified by the Admin before approving your return."
                />
              )}

              {/* Payment Receipt / Proof Upload for UPI/Bank */}
              {returnMethod !== 'Cash' && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" fontWeight="600" color="text.secondary" gutterBottom>
                    Upload Payment Receipt / Proof *
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<ReceiptIcon />}
                      color="secondary"
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                    >
                      Choose Proof File
                      <input
                        type="file"
                        hidden
                        accept="image/*,application/pdf"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            setReturnProofFile(e.target.files[0]);
                          }
                        }}
                      />
                    </Button>
                    {returnProofFile && (
                      <Chip
                        label={returnProofFile.name}
                        onDelete={() => setReturnProofFile(null)}
                        color="success"
                        variant="outlined"
                        size="medium"
                        sx={{ maxWidth: '60%', fontWeight: 'medium' }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Please upload a screenshot or copy of your receipt (Image or PDF).
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => { setOpenReturnDialog(false); setReturnMethod('Cash'); setReturnReference(''); setReturnProofFile(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleInitiateReturnAdvance}
              variant="contained"
              color="primary"
              disabled={
                returnAmount <= 0 ||
                !selectedReturnAdvance ||
                (returnMethod !== 'Cash' && (!returnReference.trim() || !returnProofFile))
              }
            >
              Submit Return Request
            </Button>
          </DialogActions>
        </Dialog>


        {/* Premium Payment Proof Receipt Preview Dialog */}
        <Dialog 
          open={!!previewImage} 
          onClose={() => setPreviewImage(null)} 
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2, px: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight="bold" color="text.primary">
              📄 Payment Proof Receipt
            </Typography>
            <IconButton onClick={() => setPreviewImage(null)} size="small" sx={{ color: 'text.secondary' }}>
              <ClearIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ py: 3, px: 3, bgcolor: alpha('#fafafa', 0.8) }}>
            {previewImage && (
              <Box display="flex" justifyContent="center" alignItems="center" p={1}>
                {previewImage.startsWith('data:application/pdf') ? (
                  <embed src={previewImage} type="application/pdf" width="100%" height="600px" style={{ borderRadius: 8, border: '1px solid rgba(0,0,0,0.05)' }} />
                ) : (
                  <img 
                    src={previewImage} 
                    alt="Payment Proof Receipt" 
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '70vh', 
                      objectFit: 'contain', 
                      borderRadius: 12,
                      boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                      border: '1px solid rgba(0,0,0,0.05)'
                    }} 
                  />
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ py: 1.5, px: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            {previewImage && (
              <Button 
                variant="outlined" 
                color="secondary" 
                href={previewImage} 
                download="payment_proof_receipt"
                sx={{ borderRadius: 2, mr: 'auto', textTransform: 'none', fontWeight: 'bold' }}
              >
                📥 Download Proof
              </Button>
            )}
            <Button 
              onClick={() => setPreviewImage(null)} 
              variant="contained" 
              color="primary"
              sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 'bold' }}
            >
              Close Preview
            </Button>
          </DialogActions>
        </Dialog>


        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AnimatedPage>
  );
};

export default Expenses;
